import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Typography, Link, Box, Stack, Alert } from '@mui/material';
import { redirect } from 'react-router-dom';

// OUR MODULES
import LoaderButton from '../../components/common/LoaderButton';
import FullscreenLayout from '../../components/layouts/FullscreenLayout';

// NETWORKING
import useLoginRequest from '../../requests/LoginRequest';

// I18N Strings
import LoginFormattedMessages from '../../messages/LoginFormattedMessages';

// IMGS
import CompleteSMSLogo from '../../images/logo-CompleteSMS.svg';

// TIMEZONES
import useTimezoneRequest from '../../requests/TimezoneRequest';
import MfaVerification from '../../components/auth/MfaVerification';

const Login = ({
  isAuthenticated,
  errorHandler,
  userHasAuthenticated,
  removeError,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [session, setSession] = useState(null);

  const loginRequest = useLoginRequest();
  const timezoneRequest = useTimezoneRequest();

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(false);
      setHasError(false);
      setErrorText('');
      redirect('/home');
    }
  }, []);

  /*
      Ensures we have some values in the form before posting
  */
  const validateForm = () => {
    return email.length > 0 && password.length > 0;
  };

  /*
      Handles changes in the form
  */
  const handleChange = (event) => {
    let value = event.target.value;
    if (event.target.id === 'email') {
      value = value.toLowerCase();
    }
    if (event.target.id === 'email') {
      setEmail(value);
    } else if (event.target.id === 'password') {
      setPassword(value);
    }
  };

  /*
    Handles the actual submission of the form
  */
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Set loading state
    setIsLoading(true);
    setHasError(false);
    setErrorText('');

    try {
      // check if both fields are populated
      if (!validateForm()) {
        setIsLoading(false);
        setHasError(true);
        setErrorText('Please fill out both fields');
        return false;
      }

      // Make network Req for Login
      const result = await loginRequest.login(email, password, errorHandler);

      const status = result.status;
      let data = null;
      try {
        data = await result.json();
      } catch (e) {
        // Do nothing
      }

      if (typeof data === 'object' && data.challenge) {
        setChallenge(data.challenge);
        setSession(data.session);
        return;
      }

      if (status === 403) {
        setIsLoading(false);
        setHasError(true);
        setErrorText('Contact your Administrator');
        return false;
      }
      // Handle login failure
      if (status === 401) {
        setIsLoading(false);
        setHasError(true);
        setErrorText('Incorrect Login Details');
        return false;
      }

      if (!status || typeof status === 'undefined') {
        throw new Error('Network Error');
      }

      await finalizeLogin(data);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setHasError(true);
      setErrorText('Network Error');
    }
  };

  const handleVerificationClose = () => {
    setChallenge(null);
    setSession(null);
    setIsLoading(false);
  };

  const handleVerified = async (result) => {
    setChallenge(null);
    setSession(null);

    await finalizeLogin(result);
  };

  const finalizeLogin = async (result) => {
    localStorage.setItem('rox_user_token', 'exists');
    localStorage.setItem('rox_user_email', email);
    // send authenticated state back to App.js
    userHasAuthenticated(true, result);
    removeError();
    let time = await timezoneRequest.getTimezoneOffset();

    // Long string for timezone, used for display
    localStorage.setItem('timeString', time.timezone_name);
    // Short string for timezone, used for luxon calculations
    localStorage.setItem('timeShort', time.time_short);
    // Short code-like string for timezone, used for unique ID for the timezone
    localStorage.setItem('timezone', time.timezone_offset);
    // Timezone identifier for date formatting
    localStorage.setItem('timeIdentifier', time.timezone_identifier || 'UTC');
    // Holds user's time range when its not a set date range but rather a descriptor. Defaults to week upon login.
    localStorage.setItem('timeRange', '7');
    // Holds user's time range for the start of a date range.
    localStorage.setItem('timeRangeStart', null);
    // Holds user's time range for the end of a date range.
    localStorage.setItem('timeRangeEnd', null);

    redirect('/home');
  };

  const generateError = () => {
    // No error, nothing to show
    if (!hasError) return null;
    // Return error
    return <Alert severity="error">{errorText}</Alert>;
  };

  return (
    <FullscreenLayout fullHeight>
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          gap: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'common.white',
            borderRadius: 4,
            paddingY: 4,
            paddingX: { xs: 2, md: 4 },
            width: 400,
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              marginBottom: 4,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src={CompleteSMSLogo}
              alt={`CompleteSMS Logo ${process.env.REACT_APP_COLOUR}`}
              width={200}
            />
          </Box>

          <Box component="form" onSubmit={(e) => handleSubmit(e)}>
            <Stack gap={2}>
              <TextField
                id="email"
                name="email"
                size="small"
                label={LoginFormattedMessages.emailText}
                variant="outlined"
                fullWidth
                type="email"
                value={email}
                onChange={handleChange}
                autoFocus
              />

              <TextField
                id="password"
                name="password"
                size="small"
                label={LoginFormattedMessages.passwordText}
                variant="outlined"
                fullWidth
                type="password"
                value={password}
                onChange={handleChange}
              />

              {generateError()}

              <LoaderButton
                disabled={false}
                type="submit"
                isLoading={isLoading}
                text={LoginFormattedMessages.loginText}
                loadingText={LoginFormattedMessages.loginLoadingText}
                sx={{ paddingY: 1 }}
              />
            </Stack>

            <Typography sx={{ textAlign: 'center', marginTop: 4 }}>
              <Link href="/forgotpassword">
                {LoginFormattedMessages.forgotPassword}
              </Link>
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography>
            {LoginFormattedMessages.signUpText}
            <Link href="/register">{LoginFormattedMessages.registerText}</Link>
          </Typography>
        </Box>
      </Stack>

      <MfaVerification
        challenge={challenge}
        session={session}
        email={email}
        open={challenge !== null}
        onClose={handleVerificationClose}
        onVerified={handleVerified}
        errorHandler={errorHandler}
      />
    </FullscreenLayout>
  );
};

export default Login;
